import { AnyType } from './common-types';

interface Mimetype {
  ext: string;
  mimeType: string;
}

interface AssetTypeItem {
  id: AssetTypeEnum;
  name: string;
  mimeTypes: Mimetype[];
}

export enum AssetTypeEnum {
  Image = 'Image',
  PDF = 'PDF',
  Video = 'Video',
  Audio = 'Audio',
  HtmlBanner = 'HTML Banner',
  PPT = 'Powerpoint presentation',
  ZIP = 'ZIP',
  Other = 'Other',
}

export const AssetType: Record<string, AssetTypeItem> = {
  Image: {
    id: AssetTypeEnum.Image,
    name: 'Image',
    mimeTypes: [
      {
        ext: 'avif',
        mimeType: 'image/avif',
      },
      {
        ext: 'bmp',
        mimeType: 'image/bmp',
      },
      {
        ext: 'gif',
        mimeType: 'image/gif',
      },
      {
        ext: 'ico',
        mimeType: 'image/vnd.microsoft.icon',
      },
      {
        ext: 'jpeg',
        mimeType: 'image/jpeg',
      },
      {
        ext: 'jpg',
        mimeType: 'image/jpeg',
      },
      {
        ext: 'png',
        mimeType: 'image/png',
      },
      {
        ext: 'svg',
        mimeType: 'image/svg+xml',
      },
      {
        ext: 'tiff',
        mimeType: 'image/tiff',
      },
      {
        ext: 'webp',
        mimeType: 'image/webp',
      },
      {
        ext: 'eps',
        mimeType: 'application/postscript',
      },
      {
        ext: 'psd',
        mimeType: 'image/vnd.adobe.photoshop',
      },
      {
        ext: 'heic',
        mimeType: 'image/heic',
      },
    ],
  },
  Video: {
    id: AssetTypeEnum.Video,
    name: 'Video',
    mimeTypes: [
      {
        ext: 'mp4',
        mimeType: 'video/mp4',
      },
      {
        ext: 'avi',
        mimeType: 'video/x-msvideo',
      },
      {
        ext: 'mpeg',
        mimeType: 'video/mpeg',
      },
      {
        ext: 'ogg',
        mimeType: 'video/ogg',
      },
      {
        ext: '3gp',
        mimeType: 'video/3gpp',
      },
      {
        ext: '3g2',
        mimeType: 'video/3gpp2',
      },
      {
        ext: 'flv',
        mimeType: 'video/x-flv',
      },
      {
        ext: 'mov',
        mimeType: 'video/quicktime',
      },
      {
        ext: 'wmv',
        mimeType: 'video/x-ms-wmv',
      },
      {
        ext: 'webm',
        mimeType: 'video/webm',
      },
      {
        ext: 'mxf',
        mimeType: 'application/mxf',
      },
    ],
  },
  Audio: {
    id: AssetTypeEnum.Audio,
    name: 'Audio',
    mimeTypes: [
      {
        ext: 'wav',
        mimeType: 'audio/wav',
      },
      {
        ext: 'wav',
        mimeType: 'audio/x-wav',
      },
      {
        ext: 'aiff',
        mimeType: 'audio/aiff',
      },
      {
        ext: 'aiff',
        mimeType: 'audio/x-aiff',
      },
      {
        ext: 'mp3',
        mimeType: 'audio/mp3',
      },
      {
        ext: 'mp3',
        mimeType: 'audio/mpeg3',
      },
      {
        ext: 'mp3',
        mimeType: 'audio/mpeg',
      },
      {
        ext: 'mp3',
        mimeType: 'audio/x-mpeg-3',
      },
      {
        ext: 'mp3',
        mimeType: 'audio/x-mpeg',
      },
      {
        ext: 'ogg',
        mimeType: 'audio/ogg',
      },
    ],
  },
  Pdf: {
    id: AssetTypeEnum.PDF,
    name: 'PDF',
    mimeTypes: [
      {
        ext: 'pdf',
        mimeType: 'application/pdf',
      },
    ],
  },
  Zip: {
    id: AssetTypeEnum.ZIP,
    name: 'Zip',
    mimeTypes: [
      {
        ext: 'zip',
        mimeType: 'application/zip',
      },
      {
        ext: 'zip',
        mimeType: 'application/x-zip-compressed',
      },
      {
        ext: 'rar',
        mimeType: 'application/x-rar-compressed',
      },
    ],
  },
  Powerpoint: {
    id: AssetTypeEnum.PPT,
    name: 'Powerpoint presentation',
    mimeTypes: [
      {
        ext: 'ppt',
        mimeType: 'application/vnd.ms-powerpoint',
      },
      {
        ext: 'pptx',
        mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      },
    ],
  },
  Other: {
    id: AssetTypeEnum.Other,
    name: 'Other',
    mimeTypes: [],
  },
};

export interface TerritoryMap {
  [territoryIndex: number]: { _id?: string; [key: string]: AnyType };
}

export interface BuyoutRightsMap {
  [rightIndex: number]: {
    _id: string;
    territories?: TerritoryMap;
    [key: string]: AnyType;
  };
}

export interface BuyoutRightsPayload {
  buyoutRights: BuyoutRightsMap;
  _id: string;
  deletedBuyoutRightIds?: string[];
  deletedBuyoutRightTerritoryIds?: {
    [key: string]: string[];
  };
}

export interface BuyoutRightsMultiEditPayload extends Omit<BuyoutRightsPayload, '_id'> {
  _id: string[];
}

export const AssetTypeFilters = Object.values(AssetType).map((item) => {
  const assetTypeKeys = Object.keys(AssetType).find((type) => item.name.toLowerCase().includes(type.toLowerCase()));
  return {
    id: assetTypeKeys,
    name: item.name,
  };
});

export const AssetMimeType = Object.values(AssetType).map((item) => {
  const mimeTypesExt = uniqueMimeTypes(item.mimeTypes);
  return mimeTypesExt;
});

export function uniqueMimeTypes(mimeTypes: Mimetype[]) {
  const mimeTypesObj = mimeTypes.map((mimeType) => ({ ...mimeType, id: mimeType.ext, name: mimeType.ext.toUpperCase() }));
  return [...new Map(mimeTypesObj.map((mimeType) => [mimeType.ext, { ...mimeType }])).values()];
}

const otherType = (mimetype: string): AssetTypeItem => ({
  id: AssetTypeEnum.Other,
  name: 'Other',
  mimeTypes: [
    {
      ext: '',
      mimeType: mimetype,
    },
  ],
});

export function assetTypeByMimetype(mimetype: string) {
  for (const key in AssetType) {
    if (Object.prototype.hasOwnProperty.call(AssetType, key)) {
      const item = AssetType[key];
      if (item.mimeTypes.some((m) => m.mimeType === mimetype)) {
        return item;
      }
    }
  }
  return otherType(mimetype);
}

export function isVideo(ext: string, mimeType?: string) {
  if (mimeType) {
    return AssetType['Video'].mimeTypes.some((m) => m.mimeType === mimeType);
  }
  return AssetType['Video'].mimeTypes.some((m) => m.ext === ext);
}
